import { useCallback, useEffect, useState } from "react";

const STORAGE_PREFIX = "hr-popup-notifications-enabled:v1";
const CHANGE_EVENT = "hr-popup-notifications-preference-changed";

function storageKey(userId) {
    const owner = String(userId ?? "anonymous").trim() || "anonymous";
    return `${STORAGE_PREFIX}:${owner}`;
}

export function readPopupNotificationsEnabled(userId) {
    if (typeof window === "undefined") return true;
    try {
        return window.localStorage.getItem(storageKey(userId)) !== "false";
    } catch {
        return true;
    }
}

export function writePopupNotificationsEnabled(userId, enabled) {
    const next = Boolean(enabled);
    if (typeof window === "undefined") return next;
    try {
        window.localStorage.setItem(storageKey(userId), String(next));
    } catch {
        // In-memory state still changes when storage is unavailable.
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
        detail: { userId: String(userId ?? "anonymous"), enabled: next },
    }));
    return next;
}

export function usePopupNotificationsPreference(userId) {
    const [enabled, setEnabledState] = useState(() => readPopupNotificationsEnabled(userId));

    useEffect(() => {
        setEnabledState(readPopupNotificationsEnabled(userId));
        const onStorage = (event) => {
            if (!event.key || event.key === storageKey(userId)) {
                setEnabledState(readPopupNotificationsEnabled(userId));
            }
        };
        const onPreferenceChange = (event) => {
            if (event.detail?.userId === String(userId ?? "anonymous")) {
                setEnabledState(Boolean(event.detail.enabled));
            }
        };
        window.addEventListener("storage", onStorage);
        window.addEventListener(CHANGE_EVENT, onPreferenceChange);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener(CHANGE_EVENT, onPreferenceChange);
        };
    }, [userId]);

    const setEnabled = useCallback(
        (next) => writePopupNotificationsEnabled(userId, next),
        [userId]
    );

    return [enabled, setEnabled];
}

